import { Bell, FlaskConical, X } from "lucide-react";
import { useEffect, useRef, useSyncExternalStore } from "react";

import { useAccountProfile } from "../account/useAccountProfile";
import { isDevEnvironment } from "../config/runtime";
import { useRuntimeConfig } from "../config/useRuntimeConfig";
import { InstallMedoPilotEntry } from "../pwa/InstallMedoPilotEntry";
import { closeCareRemindersDialog, getCareRemindersDialogOpen, subscribeToCareRemindersDialog } from "./careRemindersDialogStore";
import { describeSyntheticReminderState, formatSyntheticReminderDueTime, type SyntheticReminderState } from "./client";
import { useNotificationPermission } from "./useNotificationPermission";
import { useSyntheticReminderValidation } from "./useSyntheticReminderValidation";

const ctaButtonClassName =
  "mt-5 inline-flex min-h-11 items-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:cursor-not-allowed disabled:opacity-60";
const secondaryButtonClassName =
  "mt-4 inline-flex min-h-10 items-center gap-2 rounded-full border border-primary/25 px-4 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/5 focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:cursor-not-allowed disabled:opacity-60";
const toggleButtonClassName =
  "inline-flex min-h-9 items-center justify-center rounded-full border border-primary/25 px-4 text-sm font-semibold text-primary transition-colors hover:bg-primary/5 focus:outline-none focus:ring-4 focus:ring-primary/10 disabled:cursor-not-allowed disabled:opacity-60";

// Not device permission -- a short clause naming MedoPilot's own Care Reminders preference,
// reused across the device-status copy below so the two concepts (MedoPilot's reminder
// preference vs. browser permission) stay legible together without being conflated into one
// sentence/state. Turning this off never touches PushSubscriptions, browser/OS permission, or
// per-domain (medication/vaccination) reminder settings -- it is purely the account-level
// delivery-policy gate future reminder processing will read.
function careRemindersClause(enabled: boolean, style: "still" | "are") {
  if (enabled) {
    return style === "still" ? "Care reminders are still on in MedoPilot." : "Care reminders are on.";
  }
  return "Care reminders are currently off in MedoPilot.";
}

// The full Care Reminders management surface -- the account-level On/Off preference plus this
// device's push status / Enable Notifications / Install MedoPilot / Send Test Notification and
// the DEV-only reminder validation. Extracted verbatim from the former Settings "Care Reminders"
// card so there is exactly one implementation, opened from both Settings and Home via
// CareRemindersDialog. There are deliberately no separate Push/Email switches: email stays a
// delivery channel under the single preference and is never gated on push here.
export function CareRemindersDialogBody() {
  const { state, enabling, error, enable, iosNeedsInstall, testStatus, testMessage, testError, sendTest } =
    useNotificationPermission();
  const { profile, status: profileStatus, error: profileError, saved: profileSaved, saveProfile } = useAccountProfile();

  // Defaults to true (matches the account-service default for new/legacy accounts) while the
  // profile hasn't loaded yet, so this never flashes an incorrect "Off" before the real value
  // arrives.
  const careRemindersEnabled = profile?.care_reminders_enabled ?? true;
  const isProfileBusy = profileStatus === "loading" || profileStatus === "saving";

  const handleToggleCareReminders = () => {
    void saveProfile({ care_reminders_enabled: !careRemindersEnabled });
  };

  return (
    <div className="min-w-0">
      <p className="text-sm leading-6 text-muted-foreground">
        When this is on, MedoPilot sends your care reminders through the channels available to you, such as push
        notifications and email. Email reminders work even when push is unavailable.
      </p>

      <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl border border-border bg-surface px-4 py-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">Care Reminders</p>
          <p className="text-sm text-muted-foreground">{careRemindersEnabled ? "On" : "Off"}</p>
        </div>
        <button type="button" onClick={handleToggleCareReminders} disabled={isProfileBusy} className={toggleButtonClassName}>
          {careRemindersEnabled ? "Turn Off" : "Turn On"}
        </button>
      </div>
      {profileSaved ? (
        <p role="status" className="mt-2 text-sm leading-6 text-emerald-700">
          Saved.
        </p>
      ) : null}
      {profileError ? (
        <p role="alert" className="mt-2 text-sm leading-6 text-rose-600">
          {profileError}
        </p>
      ) : null}

      {state === "checking" ? <p className="mt-4 text-sm leading-6 text-muted-foreground">Checking notification settings…</p> : null}

      {state === "unsupported" ? (
        <div className="mt-4">
          {iosNeedsInstall ? (
            <>
              <p className="text-sm font-semibold text-foreground">
                Install MedoPilot to receive push notifications on this device.
              </p>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">
                On iPhone and iPad, add MedoPilot to your Home Screen and open it from there. You can keep using MedoPilot
                in Safari without push; email reminders are unaffected.
              </p>
              <InstallMedoPilotEntry className={ctaButtonClassName} />
            </>
          ) : (
            <p className="text-sm leading-6 text-muted-foreground">
              Push notifications are not available on this device or browser yet. Care reminders can still reach you by
              email.
            </p>
          )}
        </div>
      ) : null}

      {state === "config-unavailable" ? (
        <p className="mt-4 text-sm leading-6 text-muted-foreground">Notifications are not currently available in this MedoPilot environment.</p>
      ) : null}

      {state === "blocked" ? (
        <div className="mt-4">
          <p className="text-sm font-semibold text-foreground">Notifications are blocked on this device.</p>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">
            {careRemindersClause(careRemindersEnabled, "still")} Allow notifications for MedoPilot in your device or browser
            settings to receive them here.
          </p>
        </div>
      ) : null}

      {state === "not-enabled" ? (
        <div className="mt-4">
          <p className="text-sm font-semibold text-foreground">Enable notifications on this device</p>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">
            {careRemindersClause(careRemindersEnabled, "are")} Allow MedoPilot notifications on this device so you can
            receive them here.
          </p>
          <button type="button" onClick={() => void enable()} disabled={enabling} className={ctaButtonClassName}>
            <Bell className="size-4" aria-hidden="true" />
            {enabling ? "Enabling…" : "Enable Notifications"}
          </button>
          {error ? (
            <p role="alert" className="mt-3 text-sm leading-6 text-rose-600">
              {error}
            </p>
          ) : null}
        </div>
      ) : null}

      {state === "enabled" ? (
        <div className="mt-4">
          <p className="text-sm font-semibold text-foreground">Notifications are enabled on this device.</p>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">
            MedoPilot can send care reminders here. Make sure notifications remain allowed for MedoPilot in your device or
            browser settings.
          </p>
          <button type="button" onClick={() => void sendTest()} disabled={testStatus === "sending"} className={secondaryButtonClassName}>
            <Bell className="size-4" aria-hidden="true" />
            {testStatus === "sending" ? "Sending…" : "Send Test Notification"}
          </button>
          {testStatus === "success" ? (
            <p role="status" className="mt-3 text-sm leading-6 text-emerald-700">
              {testMessage}
            </p>
          ) : null}
          {testStatus === "empty" ? (
            <p role="status" className="mt-3 text-sm leading-6 text-muted-foreground">
              {testMessage}
            </p>
          ) : null}
          {testStatus === "error" ? (
            <p role="alert" className="mt-3 text-sm leading-6 text-rose-600">
              {testError}
            </p>
          ) : null}
        </div>
      ) : null}

      <ReminderValidationSection />
    </div>
  );
}

// The single app-root-mounted Care Reminders modal. Its open/close state is the shared
// careRemindersDialogStore; it is opened from Care Hub Settings and from the Home
// CareRemindersOffNotice, and renders exactly one CareRemindersDialogBody. Follows the existing
// MedoPilot dialog treatment (overlay + role="dialog"/aria-modal + explicit close), plus
// Escape-to-close, backdrop-click-to-close and initial focus on the close control.
export function CareRemindersDialog() {
  const open = useSyncExternalStore(subscribeToCareRemindersDialog, getCareRemindersDialogOpen);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeCareRemindersDialog();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    closeButtonRef.current?.focus();

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-end bg-slate-950/35 backdrop-blur-sm sm:place-items-center sm:px-4 sm:py-8"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          closeCareRemindersDialog();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="care-reminders-dialog-title"
        className="max-h-[92vh] w-full overflow-y-auto rounded-t-[1.5rem] bg-white p-5 shadow-[0_30px_100px_-45px_rgba(15,23,42,0.75)] sm:max-w-md sm:rounded-[1.5rem] sm:p-6"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <span className="grid size-11 place-items-center rounded-2xl bg-emerald-50 text-emerald-700">
              <Bell className="size-5" aria-hidden="true" />
            </span>
            <h2 id="care-reminders-dialog-title" className="mt-4 text-lg font-semibold text-foreground">
              Care Reminders
            </h2>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">
              Manage reminder delivery, notifications, and device settings.
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={closeCareRemindersDialog}
            aria-label="Close Care Reminders"
            className="grid size-9 shrink-0 place-items-center rounded-full border border-border bg-white text-muted-foreground transition hover:border-primary/30 hover:text-foreground focus:outline-none focus:ring-4 focus:ring-primary/10"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
        <div className="mt-5">
          <CareRemindersDialogBody />
        </div>
      </div>
    </div>
  );
}

function syntheticReminderStateToneClassName(state: SyntheticReminderState): string {
  if (state === "provider-accepted") {
    return "text-emerald-700";
  }
  if (state === "failed") {
    return "text-rose-600";
  }
  if (state === "suppressed") {
    return "text-muted-foreground";
  }
  return "text-foreground";
}

// DEV-only diagnostic (see useSyntheticReminderValidation) -- kept strictly subordinate to the
// device-enable flow and "Send Test Notification" above: no delay/title/body/ownership controls,
// no recurrence, no real reminder management. Absent entirely outside DEV, matching the backend
// routes it calls, which are also DEV-only.
function ReminderValidationSection() {
  const config = useRuntimeConfig();
  const { isAvailable, phase, reminderState, dueAt, error, isBusy, scheduleTestReminder } = useSyntheticReminderValidation();

  if (!isDevEnvironment(config?.environment) || !isAvailable) {
    return null;
  }

  const dueTime = formatSyntheticReminderDueTime(dueAt);
  const stateCopy = reminderState ? describeSyntheticReminderState(reminderState) : null;

  return (
    <div className="mt-6 border-t border-border pt-5">
      <div className="flex flex-wrap items-center gap-2">
        <FlaskConical className="size-4 text-muted-foreground" aria-hidden="true" />
        <h3 className="text-sm font-semibold text-foreground">Reminder Validation</h3>
        <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">DEV</span>
      </div>
      <p className="mt-1 text-sm leading-6 text-muted-foreground">
        Schedule a private test care reminder through the MedoPilot reminder engine.
      </p>
      <button type="button" onClick={() => void scheduleTestReminder()} disabled={isBusy} className={secondaryButtonClassName}>
        <FlaskConical className="size-4" aria-hidden="true" />
        {phase === "scheduling" ? "Scheduling…" : "Schedule Test Reminder"}
      </button>

      {stateCopy ? (
        <div className="mt-3" role="status">
          <p className={`text-sm font-semibold ${syntheticReminderStateToneClassName(reminderState as SyntheticReminderState)}`}>
            {stateCopy.label}
            {dueTime ? ` — due around ${dueTime}.` : "."}
          </p>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">{stateCopy.description}</p>
        </div>
      ) : null}

      {phase === "timed-out" ? (
        <p role="status" className="mt-3 text-sm leading-6 text-muted-foreground">
          This is taking longer than expected. Check again in a few minutes.
        </p>
      ) : null}

      {phase === "error" && error ? (
        <p role="alert" className="mt-3 text-sm leading-6 text-rose-600">
          {error}
        </p>
      ) : null}
    </div>
  );
}
